'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Check, X, Eye, EyeOff } from 'lucide-react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import SSOButtons from '@/components/SSOButtons';
import { validateSignUpInput } from '../actions/registration';
import {
  composeFullName,
  passwordRequirements,
  type FunnelIntent,
  type FunnelPlan,
} from '../schemas';
import { captureClientError } from '@/lib/error-reporting/client';
import { getAttributionSummary } from '@/lib/tracking/traffic-attribution';
import { readStoredAttribution, type LandingAttributionPayload } from '@/lib/tracking/attribution';
import { VISIT_ID_STORAGE_KEY } from '@/lib/tracking/engagement';
import { createSPAClient } from '@/lib/supabase/client';
import { useLocale, type BilingualContent } from '@/features/marketing/hooks/useLocale';
import { SUBSCRIPTION_PLANS } from '@/features/subscriptions/lib/stripe/plans';
import { RON_BILLING_HINT } from '@/features/subscriptions/lib/display-price-core';

const CURRENT_TERMS_VERSION = '3.1';
const CURRENT_PRIVACY_VERSION = '3.1';
const CURRENT_AUP_VERSION = '1.1';

// Bilingual copy for this surface — kept local (not LANDING_CONTENT) since the
// register page is auth-owned, not marketing-owned; only `useLocale`/`LocaleProvider`
// (a tiny, dependency-free primitive, `@/features/marketing/hooks/useLocale`) is
// reused, never the marketing component bundle. 2026-07-10 landing fleet rebuild,
// WS2 lane F1 — RO is the primary market (Judge 4 pack), EN is the fallback.
// Password-requirement checklist labels (from `passwordRequirements`, schemas.ts)
// are intentionally NOT localized here: that array is shared with PasswordChangeForm,
// so translating it would leak Romanian into an unrelated flow with no locale
// mechanism of its own — flagged as a follow-up, not fixed in this conservative pass.
const COPY = {
  title: { en: 'Create your account', ro: 'Creează-ți contul' },
  subtitle: {
    en: 'Start managing leads with AI automation',
    ro: 'Începe să-ți gestionezi lead-urile cu automatizări AI',
  },
  firstName: { en: 'First name', ro: 'Prenume' },
  lastName: { en: 'Last name', ro: 'Nume' },
  optional: { en: '(optional)', ro: '(opțional)' },
  phone: { en: 'Phone number', ro: 'Număr de telefon' },
  email: { en: 'Email address', ro: 'Adresă de email' },
  organization: { en: 'Organization Name', ro: 'Numele organizației' },
  organizationPlaceholder: {
    en: 'e.g., Acme Marketing Agency',
    ro: 'ex: Agenția Ta de Marketing',
  },
  organizationHint: {
    en: 'Leave blank to auto-generate from your email',
    ro: 'Lasă necompletat și se generează automat din adresa ta de email',
  },
  password: { en: 'Password', ro: 'Parolă' },
  confirmPassword: { en: 'Confirm Password', ro: 'Confirmă parola' },
  showPassword: { en: 'Show password', ro: 'Afișează parola' },
  hidePassword: { en: 'Hide password', ro: 'Ascunde parola' },
  passwordRequirementsLabel: { en: 'Password requirements:', ro: 'Cerințe pentru parolă:' },
  termsPrefix: { en: 'I agree to the', ro: 'Sunt de acord cu' },
  and: { en: 'and', ro: 'și' },
  termsOfService: { en: 'Terms of Service', ro: 'Termenii și Condițiile' },
  privacyPolicy: { en: 'Privacy Policy', ro: 'Politica de Confidențialitate' },
  acceptableUse: { en: 'Acceptable Use Policy', ro: 'Politica de Utilizare Acceptabilă' },
  planChosen: { en: 'Selected plan', ro: 'Plan ales' },
  perMonth: { en: 'month', ro: 'lună' },
  trialTerms: {
    en: '14 days on the plan you picked. Card required. You are not charged until day 14.',
    ro: '14 zile pe planul ales. Card necesar. Nu plătești până în ziua 14.',
  },
  leiHint: RON_BILLING_HINT,
  submit: { en: 'Create account', ro: 'Creează cont' },
  submitLoading: { en: 'Creating account…', ro: 'Se creează contul…' },
  alreadyHaveAccount: { en: 'Already have an account?', ro: 'Ai deja un cont?' },
  signIn: { en: 'Sign in', ro: 'Conectează-te' },
  signInToExisting: {
    en: 'Sign in to your existing account',
    ro: 'Conectează-te la contul existent',
  },
  resetPassword: { en: 'Reset your password', ro: 'Resetează-ți parola' },
  // Validation/failure copy for handleSubmit (2026-07-10 follow-up fix —
  // funnel security reviewer flagged these as hardcoded English breaking the
  // otherwise bilingual form). No em-dashes; RO informal tu.
  errorFirstNameRequired: { en: 'Please enter your first name', ro: 'Introdu-ți prenumele' },
  errorPhoneInvalid: {
    en: 'Please enter a valid phone number',
    ro: 'Introdu un număr de telefon valid',
  },
  errorTermsRequired: {
    en: 'You must accept the Terms of Service, Privacy Policy, and Acceptable Use Policy',
    ro: 'Trebuie să accepți Termenii și Condițiile, Politica de Confidențialitate și Politica de Utilizare Acceptabilă',
  },
  errorPasswordRequirements: {
    en: 'Please ensure your password meets all requirements',
    ro: 'Asigură-te că parola ta îndeplinește toate cerințele',
  },
  errorPasswordMismatch: { en: "Passwords don't match", ro: 'Parolele nu se potrivesc' },
  errorSignupFailed: {
    en: 'Signup failed. No user returned. Please try again.',
    ro: 'Înregistrarea a eșuat. Nu s-a putut crea contul. Încearcă din nou.',
  },
  errorEmailExists: {
    en: 'An account with this email already exists. If you were invited to a team, check your email for the invitation link. Otherwise, try signing in or use "Forgot password" to reset your credentials.',
    ro: 'Există deja un cont cu acest email. Dacă ai fost invitat într-o echipă, verifică emailul pentru linkul de invitație. Altfel, încearcă să te conectezi sau folosește "Ai uitat parola?" pentru a-ți reseta datele de acces.',
  },
  errorUnknown: { en: 'An unknown error occurred', ro: 'A apărut o eroare necunoscută' },
} as const satisfies Record<string, BilingualContent>;

/**
 * Tiny local reader for the consent-gated `cmg_vid` visitor cookie
 * (src/lib/tracking/consent.ts owns writing it). Deliberately does NOT import
 * consent.ts — see tracking-v3-design.md's RegisterForm.tsx contract — to
 * keep the auth surface decoupled from the tracking/consent module. Returns
 * null when absent (declined/GPC/unset, or during SSR).
 */
function readVisitorIdCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)cmg_vid=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Reads the visit id VisitBeacon stores in sessionStorage
 * (`VISIT_ID_STORAGE_KEY`, ./lib/tracking/engagement.ts) so it can be carried
 * into the signup's `landing_attribution` payload. Returns null when absent
 * (private browsing, storage quota, or the visit POST never resolved before
 * the visitor submitted this form).
 */
function readVisitIdFromSession(): string | null {
  try {
    return sessionStorage.getItem(VISIT_ID_STORAGE_KEY);
  } catch {
    // intentional: sessionStorage unavailable — nothing to carry
    return null;
  }
}

interface RegisterFormProps {
  /**
   * Funnel intent carried from the landing CTA (?intent=crm|bot|both). Persisted
   * into signUp user_metadata (`pending_intent`) so it survives the email-verification
   * hop; NOT consumed for routing decisions in this conservative pass (Judge 2
   * forensic masterplan finding 1 — post-checkout module routing is a separate,
   * out-of-scope enhancement here). Whitelisted server-side by
   * `parseFunnelIntent()` (schemas.ts) before this component ever sees it.
   */
  intent?: FunnelIntent;
  /**
   * Funnel plan carried from the landing pricing CTA (?plan=basic|pro). Persisted
   * into signUp user_metadata (`pending_plan`) so OnboardingWizard/PlanSelectionUI
   * can preselect it after email verification (visitor can still change it there).
   */
  plan?: FunnelPlan;
}

export function RegisterForm({ intent, plan }: RegisterFormProps) {
  const router = useRouter();
  const { t } = useLocale();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Check which password requirements are met
  const passwordChecks = useMemo(() => {
    return passwordRequirements.map((req) => ({
      ...req,
      met: req.test(password),
    }));
  }, [password]);

  const allRequirementsMet = passwordChecks.every((req) => req.met);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim()) {
      setError(t(COPY.errorFirstNameRequired));
      return;
    }

    if (!phoneRaw.trim() || !isValidPhoneNumber(phoneRaw.trim(), 'RO')) {
      setError(t(COPY.errorPhoneInvalid));
      return;
    }

    if (!acceptedTerms) {
      setError(t(COPY.errorTermsRequired));
      return;
    }

    // Check password requirements before submitting
    if (!allRequirementsMet) {
      setError(t(COPY.errorPasswordRequirements));
      setShowPasswordRequirements(true);
      return;
    }

    if (password !== confirmPassword) {
      setError(t(COPY.errorPasswordMismatch));
      return;
    }

    setLoading(true);

    try {
      const now = new Date().toISOString();
      const attribution = getAttributionSummary();
      // Bio-link/campaign attribution (VisitBeacon's `cmg_attribution` sessionStorage
      // carry — src/lib/tracking/attribution.ts). Distinct system from `attribution`
      // above: this one feeds the `signup_attributions` table for /admin/traffic,
      // read back in the callback route ONLY when present (cross-tab email-confirm
      // flows legitimately lose it — see that route's comment for the lower bound).
      const landingAttribution = readStoredAttribution();
      // Tracking v3: stitch in the visit/visitor identifiers when a stored
      // attribution exists to carry them alongside (both maybe-null — a
      // visitor who never saw an attributed link, or never accepted the
      // consent cookie, legitimately has neither).
      const visitId = readVisitIdFromSession();
      const visitorId = readVisitorIdCookie();
      const landingAttributionWithIds: LandingAttributionPayload | null = landingAttribution
        ? {
            ...landingAttribution,
            ...(visitId && { visitId }),
            ...(visitorId && { visitorId }),
          }
        : null;

      // Step 1: Server-side validation only (rate limits, email format, terms).
      // Does NOT call supabase.auth.signUp — that must happen client-side (Step 2).
      const validationResult = await validateSignUpInput({
        email,
        password,
        firstName,
        lastName,
        phoneRaw: phoneRaw.trim(),
        organizationName: organizationName || undefined,
        terms_version: CURRENT_TERMS_VERSION,
        terms_accepted_at: now,
        privacy_version: CURRENT_PRIVACY_VERSION,
        privacy_accepted_at: now,
        aup_version: CURRENT_AUP_VERSION,
        aup_accepted_at: now,
        attribution,
      });

      if (validationResult?.error) {
        setError(validationResult.error);
        setLoading(false);
        return;
      }

      // Step 2: PKCE signup via browser client.
      // CRITICAL: auth.signUp() MUST run in the browser, not in a Server Action.
      // @supabase/ssr generates a code_verifier and persists it via the storage
      // adapter. In a Server Action the setAll() cookie handler is wrapped in
      // try/catch that silently swallows errors (server.ts:22-25), so the verifier
      // cookie can be lost before reaching the browser. When the user later clicks
      // the confirmation email the callback has a ?code= but no verifier →
      // Supabase Auth rejects: "both auth code and code verifier should be non-empty"
      // (Sentry NEXT-9Q, 2026-05-05). Using createSPAClient() here guarantees the
      // verifier is stored reliably by the browser's own storage mechanism.
      const supabase = createSPAClient();
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            organizationName: organizationName || `${email.split('@')[0]}'s Organization`,
            full_name: composeFullName(firstName, lastName),
            phone: phoneRaw.trim(),
            terms_version: CURRENT_TERMS_VERSION,
            terms_accepted_at: now,
            privacy_version: CURRENT_PRIVACY_VERSION,
            privacy_accepted_at: now,
            aup_version: CURRENT_AUP_VERSION,
            aup_accepted_at: now,
            ...(attribution && { attribution }),
            ...(landingAttributionWithIds && { landing_attribution: landingAttributionWithIds }),
            // Funnel intent/plan (2026-07-10, WS2 lane F1) — persisted as raw
            // user_metadata so it survives the email-verification hop (the only
            // reliable channel: no session/cookie exists yet, and the visitor may
            // click the link on a different device). Read back in
            // src/app/(onboarding)/onboarding/page.tsx via supabase.auth.getUser().
            ...(plan && { pending_plan: plan }),
            ...(intent && { pending_intent: intent }),
          },
          emailRedirectTo: `${window.location.origin}/api/auth/callback`,
        },
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError(t(COPY.errorSignupFailed));
        setLoading(false);
        return;
      }

      // Detect Supabase anti-enumeration fake success (email already exists)
      if (authData.user.identities && authData.user.identities.length === 0) {
        setError(t(COPY.errorEmailExists));
        setLoading(false);
        return;
      }

      // Success — redirect to verification pending page; keep loading active
      router.push(`/auth/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err: Error | unknown) {
      captureClientError(err, 'client_runtime', {
        component: 'RegisterForm',
        action: 'signUp',
      }).catch(() => {});
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t(COPY.errorUnknown));
      }
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title - locale (RO/EN) comes from the geo-detected LocaleProvider default set in page.tsx */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-white">{t(COPY.title)}</h3>
        <p className="mt-2 text-sm text-gray-400">{t(COPY.subtitle)}</p>
      </div>

      {error && (
        <div className="p-4 text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded-lg space-y-2">
          <p>{error}</p>
          {error.includes('already exists') && (
            <div className="flex flex-col gap-1.5 pt-1 border-t border-red-500/20">
              <Link
                href={`/auth/login${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                prefetch={false}
                className="text-yellow-500 hover:text-amber-400 font-medium underline transition-colors"
              >
                {t(COPY.signInToExisting)}
              </Link>
              <Link
                href="/auth/forgot-password"
                className="text-yellow-500 hover:text-amber-400 font-medium underline transition-colors"
              >
                {t(COPY.resetPassword)}
              </Link>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
              {t(COPY.firstName)} <span className="text-yellow-500">*</span>
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
              placeholder="Maria"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
              {t(COPY.lastName)} <span className="text-gray-500 text-xs">{t(COPY.optional)}</span>
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
              placeholder="Popescu"
            />
          </div>
        </div>

        {/* Phone stays required — deliberate 2026-06-30 design decision
            (kb: register-schema-phone-required), not something to remove here. */}
        <div>
          <label htmlFor="phoneRaw" className="block text-sm font-medium text-gray-300 mb-2">
            {t(COPY.phone)} <span className="text-yellow-500">*</span>
          </label>
          <input
            id="phoneRaw"
            name="phoneRaw"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            required
            value={phoneRaw}
            onChange={(e) => setPhoneRaw(e.target.value)}
            className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
            placeholder="0712 345 678"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            {t(COPY.email)}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="organizationName"
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            {t(COPY.organization)} <span className="text-gray-500 text-xs">{t(COPY.optional)}</span>
          </label>
          <input
            id="organizationName"
            name="organizationName"
            type="text"
            autoComplete="organization"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder={t(COPY.organizationPlaceholder)}
            className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
          />
          <p className="mt-1.5 text-xs text-gray-500">{t(COPY.organizationHint)}</p>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            {t(COPY.password)}
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setShowPasswordRequirements(true)}
              className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 pr-12 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-200 transition-colors"
              aria-label={showPassword ? t(COPY.hidePassword) : t(COPY.showPassword)}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {/* Password requirements checklist — item labels stay English (see COPY comment above) */}
          {showPasswordRequirements && (
            <div className="mt-3 p-3 rounded-lg bg-black/30 border border-yellow-500/20">
              <p className="text-xs font-medium text-gray-400 mb-2">
                {t(COPY.passwordRequirementsLabel)}
              </p>
              <ul className="space-y-1.5">
                {passwordChecks.map((req) => (
                  <li
                    key={req.id}
                    className={`flex items-center gap-2 text-xs transition-colors ${
                      req.met ? 'text-green-400' : 'text-gray-500'
                    }`}
                  >
                    {req.met ? (
                      <Check className="h-3.5 w-3.5 flex-shrink-0" />
                    ) : (
                      <X className="h-3.5 w-3.5 flex-shrink-0" />
                    )}
                    <span>{req.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
            {t(COPY.confirmPassword)}
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 pr-12 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-200 transition-colors"
              aria-label={showConfirmPassword ? t(COPY.hidePassword) : t(COPY.showPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="h-4 w-4 rounded border-yellow-500/30 bg-black/70 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-black"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="text-gray-400">
              {t(COPY.termsPrefix)}{' '}
              <Link
                href="/legal/terms"
                className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
                target="_blank"
              >
                {t(COPY.termsOfService)}
              </Link>
              ,{' '}
              <Link
                href="/legal/privacy"
                className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
                target="_blank"
              >
                {t(COPY.privacyPolicy)}
              </Link>
              , {t(COPY.and)}{' '}
              <Link
                href="/legal/acceptable-use"
                className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
                target="_blank"
              >
                {t(COPY.acceptableUse)}
              </Link>
            </label>
          </div>
        </div>

        {/* Chosen plan + trial terms — only when the landing pricing CTA carried a
            plan (?plan=basic|pro). Purely informational; the visitor still confirms
            the actual plan in OnboardingWizard/PlanSelectionUI after verification. */}
        {plan && (
          <div className="rounded-lg border border-yellow-500/25 bg-yellow-500/5 p-3 space-y-1">
            <p className="text-xs font-semibold text-yellow-500">
              {t(COPY.planChosen)}: {SUBSCRIPTION_PLANS[plan].displayName} · €
              {SUBSCRIPTION_PLANS[plan].price}/{t(COPY.perMonth)}
            </p>
            <p className="text-xs text-gray-400">{t(COPY.trialTerms)}</p>
            <p className="text-xs text-gray-400">{t(COPY.leiHint)}</p>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center rounded-lg border border-transparent bg-gradient-to-r from-yellow-400 to-amber-600 py-3 px-4 text-sm font-bold text-black shadow-lg hover:from-yellow-500 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? t(COPY.submitLoading) : t(COPY.submit)}
          </button>
        </div>
      </form>

      <SSOButtons onError={setError} />

      <div className="text-center text-sm border-t border-yellow-500/20 pt-6">
        <span className="text-gray-400">{t(COPY.alreadyHaveAccount)}</span>{' '}
        <Link
          href="/auth/login"
          className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
        >
          {t(COPY.signIn)}
        </Link>
      </div>
    </div>
  );
}
